import {
  DAG_SLUTT_MINUTT,
  DAG_START_MINUTT,
  VARSEL_GRENSE_MINUTT,
} from '@/data/timeCosts';
import { EventBus } from '@/systems/eventBus';

/**
 * GameClock — spillets hjerte (fase 1, Mad TV-mekanikk).
 *
 * Klokka tikker IKKE i sanntid: tid forbrukes av handlinger via
 * `brukTid(minutter, handling)`. Spilldagen går 08:00–17:00; når
 * klokka når 17:00 emittes 'time:dayEnded' én gang, og scenen åpner
 * dagsoppsummeringen. Tidskostnadene bor i data/timeCosts.ts.
 */

export interface TidAvansertHendelse {
  /** Minutter siden midnatt før handlingen. */
  fra: number;
  /** Minutter siden midnatt etter handlingen (klemt til 17:00). */
  til: number;
  /** Faktisk forbrukte minutter (overskytende kuttes ved dagslutt). */
  minutter: number;
  /** Valgfri beskrivelse av handlingen som brukte tida. */
  handling?: string;
}

export interface DagSluttHendelse {
  minutt: number;
}

export type KlokkeEvents = {
  'time:advanced': TidAvansertHendelse;
  'time:dayEnded': DagSluttHendelse;
};

/** Formaterer minutter siden midnatt som «HH:MM». */
export function formaterKlokkeslett(minutt: number): string {
  const t = Math.floor(minutt / 60);
  const m = minutt % 60;
  return `${String(t).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export class GameClock {
  private minutt = DAG_START_MINUTT;
  private dagSluttVarslet = false;

  constructor(private bus: EventBus<KlokkeEvents>) {}

  /** Minutter siden midnatt akkurat nå. */
  get naa(): number {
    return this.minutt;
  }

  get klokkeslett(): string {
    return formaterKlokkeslett(this.minutt);
  }

  get minutterIgjen(): number {
    return DAG_SLUTT_MINUTT - this.minutt;
  }

  get underEnTimeIgjen(): boolean {
    return !this.dagenErOver && this.minutterIgjen <= VARSEL_GRENSE_MINUTT;
  }

  get dagenErOver(): boolean {
    return this.minutt >= DAG_SLUTT_MINUTT;
  }

  /**
   * Forbruker tid. Klemmer ved 17:00 og emitter 'time:dayEnded' første
   * gang dagslutt nås. Etter dagslutt er videre brukTid en no-op til
   * nyDag() kalles.
   */
  brukTid(minutter: number, handling?: string): void {
    if (minutter <= 0 || this.dagenErOver) return;
    const fra = this.minutt;
    const til = Math.min(fra + minutter, DAG_SLUTT_MINUTT);
    this.minutt = til;
    this.bus.emit('time:advanced', {
      fra,
      til,
      minutter: til - fra,
      handling,
    });
    if (til >= DAG_SLUTT_MINUTT && !this.dagSluttVarslet) {
      this.dagSluttVarslet = true;
      this.bus.emit('time:dayEnded', { minutt: til });
    }
  }

  /** Nullstiller klokka til 08:00 — kalles når spilldagen byttes. */
  nyDag(): void {
    this.minutt = DAG_START_MINUTT;
    this.dagSluttVarslet = false;
  }

  /**
   * Persistens: setter klokka direkte (uten å emitte) ved innlasting
   * av lagret tilstand. Klemmes til spilldagen.
   */
  settMinutt(minutt: number): void {
    this.minutt = Math.min(
      Math.max(Math.floor(minutt), DAG_START_MINUTT),
      DAG_SLUTT_MINUTT,
    );
    this.dagSluttVarslet = this.minutt >= DAG_SLUTT_MINUTT;
  }
}

/** Delt buss for klokke-hendelser — andre systemer lytter her. */
export const klokkeBus = new EventBus<KlokkeEvents>();

/** Singleton-klokka spillet bruker. */
export const gameClock = new GameClock(klokkeBus);
